/**
 * Rule: a SYSTEM step's mapped OUTPUT parameter must have a value source.
 *
 * Mapping an output only declares WHICH workflow parameter the step writes. The
 * value comes from the symbol on `step.outputParameters[].value`, and a
 * freshly-mapped output is created as an EMPTY fixed-value symbol (see
 * `workflow-provider.tsx SetStepParamsAndMappings` — the panel's "now pick a
 * source" state). Codegen's Fixed branch then emits `= ""`
 * (`WorkflowStepGenerator.SetOutputs`), so the parameter is empty for the whole
 * rest of the process — and nothing else complains: the mapping exists, the
 * action runs, project validation passes.
 *
 * That shipped in KOA-17362 smoke 12: the "persist the return request" system
 * step mapped `ReturnRequestId` and the "create the return record" step mapped
 * `ReturnRecordId`, both with no source, so every later step keyed on an empty
 * id. This rule makes that state visible at LINT time instead of at runtime.
 *
 * Scope — SYSTEM STEPS ONLY, i.e. the exact `bpmn:Task` type, exactly like
 * `system-step-has-action`. A USER task's outputs are written by its INTERACTION
 * buttons or by the assignee on the generated task form; an empty symbol there is
 * the CORRECT persisted state, not a defect.
 *
 * Unbound means: no symbol at all, or a `fixed` (4) symbol with no `fixedValue`.
 * A deliberate constant (`INTERACTION … SET p=true` writes a fixed value) and any
 * other category (action result, workflow parameter, default) are all bound.
 *
 * CONSERVATIVE, like its sibling: when the workflow context is not ready or the
 * JSON step cannot be resolved, SKIP — this is an error-level rule and must not
 * fire a false positive mid-load. The linter re-runs on the next diagram event.
 */
const { is } = require("bpmnlint-utils");

const ESYMBOL_CATEGORY_FIXED = 4;

function resolveStep(ctx, nodeId) {
  const sd = ctx && ctx.providerInstance ? ctx.providerInstance : null;
  if (!sd) return { ready: false, step: null };

  const workflowProvider = sd.workflowProvider ? sd.workflowProvider : null;
  const ctxValues =
    typeof sd.getScreenDesignContextProviderValues === "function" ? sd.getScreenDesignContextProviderValues() : null;
  const selectedWf =
    (workflowProvider && typeof workflowProvider.getSelectedWf === "function" && workflowProvider.getSelectedWf()) ||
    (ctxValues && ctxValues.selectedWf) ||
    null;
  if (!selectedWf) return { ready: false, step: null };

  let step = null;
  if (workflowProvider && typeof workflowProvider.findStepRecursively === "function") {
    try {
      step = workflowProvider.findStepRecursively(selectedWf, (s) => s && s.id === nodeId);
    } catch {
      step = null;
    }
  }
  if (!step) {
    const steps = ctx && typeof ctx.getWfSteps === "function" ? ctx.getWfSteps() : undefined;
    if (Array.isArray(steps)) step = steps.find((s) => s && s.id === nodeId) || null;
  }
  return { ready: true, step, selectedWf };
}

/** Name of a workflow parameter by id, for a message the user can act on. */
function parameterName(selectedWf, parameterId) {
  const params = selectedWf && Array.isArray(selectedWf.parameters) ? selectedWf.parameters : [];
  const found = params.find((p) => p && p.id === parameterId);
  return found && found.name ? found.name : parameterId;
}

function isUnboundValue(value) {
  if (!value) return true;
  if (value.category === undefined || value.category === null) return true;
  if (value.category !== ESYMBOL_CATEGORY_FIXED) return false;
  return value.fixedValue === undefined || value.fixedValue === null || String(value.fixedValue).length === 0;
}

module.exports = function stepOutputUnbound({ ctx }) {
  return {
    check(node, reporter) {
      if (!node) return;
      const bo = node.businessObject || node;
      if (bo.$type !== "bpmn:Task") return;
      if (is(node, "bpmn:SubProcess") || is(node, "bpmn:CallActivity")) return;

      const { ready, step, selectedWf } = resolveStep(ctx, node.id);
      if (!ready || !step) return;

      const outputs = Array.isArray(step.outputParameters) ? step.outputParameters : [];
      if (outputs.length === 0) return;

      const unbound = outputs
        .filter((p) => p && isUnboundValue(p.value))
        .map((p) => parameterName(selectedWf, p.id))
        .filter(Boolean);

      if (unbound.length > 0) {
        reporter.report(
          node.id,
          `Step output(s) ${unbound.join(", ")} have no value source and will be written as an empty string, ` +
            `so every later step that reads them reads nothing. ` +
            `Give each one a source — the result field of an action this step runs.`
        );
      }
    }
  };
};
